import React from "react";
import { Link } from "react-router-dom";
import { useSite } from "../../context/site";
import { track } from "../../lib/analytics";
import { Eyebrow, Cta } from "./kit";
import { ArrowField } from "./motion";
import { GROWTH_SITUATIONS } from "../../lib/content";

const OtherProblems = ({ current }) => {
  const idx = GROWTH_SITUATIONS.findIndex((s) => s.href === current);
  const others = GROWTH_SITUATIONS.filter((_, i) => i !== idx);
  return (
    <section data-testid="situation-pathway" className="border-t border-white/10 bg-[#161A15]">
      <div className="mx-auto max-w-[1360px] px-6 py-16 md:px-12 md:py-20 lg:px-16">
        <p className="font-body text-sm font-semibold uppercase tracking-[0.18em] text-[#5f8f10]">Explore the other problems</p>
        <div className="mt-8 grid gap-px border border-white/12 bg-white/12 sm:grid-cols-3">
          {others.map((s) => (
            <Link key={s.id} to={s.href} data-testid={`situation-other-${s.id}`} className="group flex flex-col bg-[#161A15] p-7 transition-colors duration-200 hover:bg-white/[0.04]">
              <span className="font-display text-3xl leading-none text-[#5f8f10]">{s.number}</span>
              <h3 className="font-display mt-4 text-xl uppercase leading-[1.02] tracking-tight text-white transition-colors duration-200 group-hover:text-[#5f8f10]">{s.title}</h3>
              <span className="font-body mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-white/60">
                View <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-1">→</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const SectionBlock = ({ section }) => (
  <section className="border-b border-black/10 bg-[#F7F6F2]">
    <div className="mx-auto grid max-w-[1360px] gap-8 px-6 py-16 md:grid-cols-12 md:px-12 md:py-24 lg:px-16">
      <div className="md:col-span-4">
        {section.kicker && <Eyebrow>{section.kicker}</Eyebrow>}
        <h2 className="font-display mt-4 text-3xl uppercase leading-[0.95] tracking-tight text-black md:text-4xl">{section.heading}</h2>
      </div>
      <div className="md:col-span-8">
        {section.body?.map((p, i) => (
          <p key={i} className="font-body mb-5 max-w-2xl text-lg font-normal leading-relaxed text-black/85">{p}</p>
        ))}
        {section.items && (
          <ul className="mt-4 grid border border-black/20 sm:grid-cols-2">
            {section.items.map((it, i) => (
              <li key={i} className={`font-body flex items-center gap-3 p-5 text-base font-normal text-black ${i % 2 === 0 ? "sm:border-r sm:border-black/20" : ""} border-b border-black/20 sm:[&:nth-last-child(-n+2)]:border-b-0`}>
                <span className="h-2.5 w-2.5 shrink-0 bg-[#5f8f10]" />{it}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </section>
);

export const SituationPage = ({ data }) => {
  const { openDiagnostic } = useSite();
  const markSrc = ["01", "02", "03", "04"].includes(String(data.number)) ? `/assets/growth-${data.number}.png` : null;
  return (
    <div className="font-body bg-white">
      <section className="relative overflow-hidden bg-[#161A15] pb-24 pt-36 text-white md:pt-44">
        <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] lg:block">
          <ArrowField active className="h-full w-full" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#161A15 0%, rgba(22,26,21,0.55) 26%, rgba(22,26,21,0) 62%)" }} />
        </div>
        <div className="relative z-10 mx-auto max-w-[1360px] px-6 md:px-12 lg:px-16">
          <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mb-6">
            <ol className="flex flex-wrap items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.14em] text-white/50">
              <li><Link to="/" className="transition-colors hover:text-[#5f8f10]">Home</Link></li>
              <li className="flex items-center gap-2"><span aria-hidden className="text-white/30">/</span><Link to="/what-we-solve" className="transition-colors hover:text-[#5f8f10]">What We Solve</Link></li>
              <li className="flex items-center gap-2"><span aria-hidden className="text-white/30">/</span><span className="text-[#5f8f10]">{data.title}</span></li>
            </ol>
          </nav>
          <div className="max-w-3xl">
            <p className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">Growth situation · {data.number}</p>
            <h1 className="font-display mt-4 max-w-[16ch] text-5xl leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">{data.title}</h1>
            <div className="mt-8 max-w-2xl space-y-4">
              {data.intro.map((p, i) => (<p key={i} className="font-body text-xl font-normal leading-relaxed text-white/90">{p}</p>))}
            </div>
            <Cta primary onClick={() => { track("situation_page_cta_clicked", { situation: data.title }); openDiagnostic(); }} testId="situation-cta" className="mt-10">
              Bring Us Your Growth Problem
            </Cta>
          </div>
        </div>
      </section>

      {data.sections.map((s, i) => (<SectionBlock key={i} section={s} />))}

      <section className="border-t border-white/10 bg-[#161A15]">
        <div className="mx-auto max-w-[1360px] px-6 py-10 md:px-12 md:py-12 lg:px-16">
          <Link
            to={`/insights?situation=${encodeURIComponent(data.title)}`}
            data-testid="situation-switchboard-link"
            onClick={() => track("situation_switchboard_link", { situation: data.title })}
            className="group inline-flex items-center gap-3 font-body text-sm font-semibold uppercase tracking-[0.14em] text-[#5f8f10]"
          >
            Read {data.title} thinking on the Switchboard
            <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-1">→</span>
          </Link>
        </div>
      </section>

      <OtherProblems current={`/what-we-solve/${data.slug}`} />

      <section className="border-t-2 border-[#5f8f10] bg-[#161A15]">
        <div className="mx-auto grid max-w-[1360px] items-center gap-10 px-6 py-24 md:grid-cols-12 md:px-12 md:py-28 lg:px-16">
          <h2 className="font-display md:col-span-8 text-4xl leading-[0.95] tracking-tight text-white md:text-5xl lg:text-6xl">Start with the situation your business is facing.</h2>
          <div className="md:col-span-4 md:flex md:justify-end">
            <Cta primary onClick={() => { track("situation_page_cta_clicked", { situation: data.title, position: "closing" }); openDiagnostic(); }} testId="situation-closing-cta">Bring Us Your Growth Problem</Cta>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SituationPage;
